import React from "react";
import cn from "classnames";

type BadgeVariant = "filled" | "soft" | "outline";
export type BadgeColor =
  | "gray"
  | "info"
  | "positive"
  | "notice"
  | "negative"
  | "primary"
  | "yellow"
  | "indigo"
  | "pink"
  | "brown"
  | "lime"
  | "cyan";

interface BadgeProps {
  variant?: BadgeVariant;
  color?: BadgeColor;
  text: string;
  icon?: React.ReactNode;
  dot?: boolean;
  clickable?: boolean;
  className?: string;
  onRemove?: () => void;
  title?: string;
}

const colorVariantsMap: Record<BadgeVariant, Record<BadgeColor, string>> = {
  filled: {
    gray: "bg-grey-500 text-white",
    info: "bg-blue-500 text-white",
    positive: "bg-green-500 text-white",
    notice: "bg-orange-500 text-white",
    negative: "bg-red-500 text-white",
    primary: "bg-brand-500 text-white",
    yellow: "bg-yellow-500 text-black",
    indigo: "bg-indigo-500 text-white",
    pink: "bg-pink-500 text-white",
    brown: "bg-brown-500 text-white",
    lime: "bg-lime-500 text-black",
    cyan: "bg-cyan-500 text-white",
  },
  soft: {
    gray: "bg-grey-100 text-grey-900 dark:text-grey-500",
    info: "bg-blue-100 text-blue-900 dark:text-blue-500",
    positive: "bg-green-100 text-green-900 dark:text-green-500",
    notice: "bg-orange-100 text-orange-900 dark:text-orange-500",
    negative: "bg-red-100 text-red-900 dark:text-red-500",
    primary: "bg-brand-100 text-brand-900 dark:text-brand-500",
    yellow: "bg-yellow-100 text-yellow-900 dark:text-yellow-500",
    indigo: "bg-indigo-100 text-indigo-900 dark:text-indigo-500",
    pink: "bg-pink-100 text-pink-900 dark:text-pink-500",
    brown: "bg-brown-100 text-brown-900 dark:text-brown-500",
    lime: "bg-lime-100 text-lime-900 dark:text-lime-500",
    cyan: "bg-cyan-100 text-cyan-900 dark:text-cyan-500",
  },
  outline: {
    gray: "bg-transparent border border-grey-200 text-grey-900 dark:text-grey-500 dark:border-grey-500",
    info: "bg-transparent border border-blue-200 text-blue-900 dark:text-blue-500 dark:border-blue-500",
    positive:
      "bg-transparent border border-green-200 text-green-900 dark:text-green-500 dark:border-green-500",
    notice: "bg-transparent border border-orange-200 text-orange-900 dark:text-orange-500 dark:border-orange-500",
    negative:
      "bg-transparent border border-red-200 text-red-900 dark:text-red-500 dark:border-red-500",
    primary: "bg-transparent border border-brand-200 text-brand-900 dark:text-brand-500 dark:border-brand-500",
    yellow: "bg-transparent border border-yellow-200 text-yellow-900 dark:text-yellow-500 dark:border-yellow-500",
    indigo: "bg-transparent border border-indigo-200 text-indigo-900 dark:text-indigo-500 dark:border-indigo-500",
    pink: "bg-transparent border border-pink-200 text-pink-900 dark:text-pink-500 dark:border-pink-500",
    brown: "bg-transparent border border-brown-200 text-brown-900 dark:text-brown-500 dark:border-brown-500",
    lime: "bg-transparent border border-lime-200 text-lime-900 dark:text-lime-500 dark:border-lime-500",
    cyan: "bg-transparent border border-cyan-200 text-cyan-900 dark:text-cyan-500 dark:border-cyan-500",
  },
};

const dotColorMap: Record<BadgeColor, string> = {
  gray: "bg-grey-500",
  info: "bg-blue-500",
  positive: "bg-green-500",
  notice: "bg-orange-500",
  negative: "bg-red-500",
  primary: "bg-brand-500",
  yellow: "bg-yellow-500",
  indigo: "bg-indigo-500",
  pink: "bg-pink-500",
  brown: "bg-brown-500",
  lime: "bg-lime-500",
  cyan: "bg-cyan-500",
};

export default function Badge({
  variant = "filled",
  color = "gray",
  icon,
  dot,
  clickable,
  text,
  className,
  onRemove,
  title,
}: BadgeProps) {
  const variantClasses = colorVariantsMap[variant][color];
  const hasLeadingElement = icon || dot;
  const hasTrailingElement = !!onRemove;
  const paddingClasses = cn(
    "py-[2px]",
    hasLeadingElement ? "pl-1.5" : "pl-2",
    hasTrailingElement ? "pr-1" : "pr-2",
  );

  return (
    <div className="flex items-center">
      <span
        title={title !== undefined ? title : text}
        className={cn(
          "text-xs leading-4 xl:leading-5 font-medium rounded-full flex items-center justify-center gap-1 max-w-full",
          paddingClasses,
          variantClasses,
          clickable && "cursor-pointer",
          className,
        )}>
        {icon && <span className="flex items-center shrink-0">{icon}</span>}
        {dot && (
          <span
            className={cn("w-1.5 h-1.5 rounded-full shrink-0", variant === "filled" ? "bg-white" : dotColorMap[color])}
          />
        )}
        <span className="truncate">{text}</span>
        {onRemove && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onRemove();
            }}
            className="flex items-center shrink-0 cursor-pointer opacity-60 hover:opacity-100 transition-opacity">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M4.5 4.5L9.5 9.5M9.5 4.5L4.5 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        )}
      </span>
    </div>
  );
}
